import { useRef, useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ChevronRight } from 'lucide-react';
import { FEED_TYPES } from '../../hooks/notifications/useCombinedFeed';

/**
 * Filter bar with horizontal scrolling pills for filtering the feed
 */
const FeedFilterBar = ({ 
  activeFilter, 
  onFilterChange, 
  counts = {},
  className = '',
}) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  // Filter options with labels
  const baseFilterOptions = [
    { 
      value: FEED_TYPES.ALL, 
      label: t('filter_all_posts', 'All Posts'),
    },
    { 
      value: FEED_TYPES.LOST, 
      label: t('filter_lost_animals', 'Lost Animals'),
    },
    { 
      value: FEED_TYPES.FOUND, 
      label: t('filter_found_animals', 'Found Animals'),
    },
    { 
      value: FEED_TYPES.PSA, 
      label: t('filter_psa', 'PSAs'),
    },
    { 
      value: FEED_TYPES.PET_SERVICE, 
      label: t('filter_pet_services', 'Pet Services'),
    },
    { 
      value: FEED_TYPES.EMERGENCY, 
      label: t('filter_emergency', 'Emergency Alerts'),
    },
  ];

  // Add "My Posts" filter only for authenticated users
  const filterOptions = isAuthenticated 
    ? [
        ...baseFilterOptions, 
        { 
          value: FEED_TYPES.MY_POSTS, 
          label: t('filter_my_posts', 'My Posts'),
        }
      ]
    : baseFilterOptions;

  // Check if scroll indicator should be shown
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const checkScroll = () => {
      const hasOverflow = container.scrollWidth > container.clientWidth;
      const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
      setShowScrollIndicator(hasOverflow && !isAtEnd);
    };

    checkScroll();
    container.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  const handleFilterClick = (filterValue) => {
    onFilterChange(filterValue);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Scrollable filter pills container */}
      <div 
        ref={scrollContainerRef}
        className="flex gap-1.5 sm:gap-2 overflow-x-auto px-3 sm:px-4 pb-3 pt-2 relative show-scrollbar"
      >
        {filterOptions.map((option) => {
          const isActive = activeFilter === option.value;
          
          return (
            <button
              key={option.value}
              onClick={() => handleFilterClick(option.value)}
              className={`
                flex-shrink-0 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium 
                transition-colors min-h-[32px] sm:min-h-[36px] whitespace-nowrap
                ${isActive 
                  ? 'bg-slate-700 text-white shadow-md' 
                  : 'bg-white text-muted-foreground hover:bg-white/80 border border-border'
                }
              `}
              aria-pressed={isActive}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {/* Scroll indicator */}
      {showScrollIndicator && (
        <div 
          className="absolute right-0 top-0 bottom-0 w-12 pointer-events-none 
                     flex items-center justify-end pr-2"
          aria-hidden="true"
        >
          <div className="w-7 h-7 rounded-full bg-gray-50 shadow-md flex items-center justify-center">
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(FeedFilterBar);
